/**
 * Unilar Serviço ao Cliente
 * Carousel only on mobile (≤749px). Grid layout on tablet/desktop.
 */

class ServicoClienteSlider extends HTMLElement {
  /** @type {import('fancyapps').Carousel | null} */
  #carousel = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #heightSyncBound = false;
  #hasActivated = false;
  #carouselRafId = null;
  /** @type {Set<HTMLElement>} */
  #managedSlides = new Set();
  #mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  #boundBreakpointHandler = this.#handleBreakpoint.bind(this);

  #syncSlideAccessibility = () => {
    if (!this.#carousel || typeof this.#carousel.getVisibleSlides !== 'function') return;

    const visibleSlides = this.#carousel.getVisibleSlides(0);

    this.#carousel.slides.forEach((slide) => {
      const element = slide.el;
      if (!(element instanceof HTMLElement)) return;

      this.#managedSlides.add(element);

      if (visibleSlides.has(slide)) {
        element.removeAttribute('inert');
        element.setAttribute('aria-hidden', 'false');
      } else {
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      }
    });
  };

  /** @type {import('fancyapps').Carousel | null} */
  carousel = null;

  get activated() {
    return this.#hasActivated;
  }

  connectedCallback() {
    this.#mobileMediaQuery.addEventListener('change', this.#boundBreakpointHandler);

    if (
      this.dataset.lazyInit !== 'true' ||
      window.Shopify?.designMode === true ||
      !('IntersectionObserver' in window)
    ) {
      this.#activate();
    } else {
      this.#observeForActivation();
      this.#handleBreakpoint();
    }

    this.#bindHeightSync();
  }

  disconnectedCallback() {
    this.#mobileMediaQuery.removeEventListener('change', this.#boundBreakpointHandler);
    this.#disconnectIntersectionObserver();
    this.#hasActivated = false;
    this.#destroy();
  }

  #destroy() {
    this.#cancelCarouselWait();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#heightSyncBound = false;
    this.#destroyCarousel();
  }

  #destroyCarousel() {
    this.#cancelCarouselWait();

    if (this.#carousel) {
      this.#carousel.off('ready change settle refresh', this.#syncSlideAccessibility);
      this.#clearSlideAccessibility();
      this.#carousel.destroy();
      this.#carousel = null;
    }

    this.carousel = null;
    this.classList.remove('is-carousel-active');
    this.#resetCarouselStyles();
  }

  #clearSlideAccessibility() {
    this.#managedSlides.forEach((slide) => {
      slide.removeAttribute('inert');
      slide.removeAttribute('aria-hidden');
    });
    this.#managedSlides.clear();
  }

  #cancelCarouselWait() {
    if (this.#carouselRafId === null) return;

    cancelAnimationFrame(this.#carouselRafId);
    this.#carouselRafId = null;
  }

  #observeForActivation() {
    if (this.#intersectionObserver || this.#hasActivated) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#activate();
        }
      },
      {
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    this.#intersectionObserver.observe(this);
  }

  #disconnectIntersectionObserver() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
  }

  #activate() {
    if (this.#hasActivated) return;

    this.#hasActivated = true;
    this.#disconnectIntersectionObserver();
    this.#handleBreakpoint();
  }

  #resetCarouselStyles() {
    const track = this.querySelector('.f-carousel__track');

    if (track) {
      track.style.transform = '';
    }

    this.querySelectorAll('.f-carousel__slide').forEach((slide) => {
      slide.style.left = '';
      slide.style.right = '';
      slide.style.top = '';
    });
  }

  #equalizeHeights() {
    if (!this.#hasActivated) return;

    const cards = this.querySelectorAll('.servico-cliente__card-inner');
    if (!cards.length) return;

    cards.forEach((card) => {
      card.style.minHeight = '';
    });

    const maxHeight = Math.max(
      ...Array.from(cards, (card) => card.getBoundingClientRect().height)
    );

    if (maxHeight > 0) {
      cards.forEach((card) => {
        card.style.minHeight = `${maxHeight}px`;
      });
    }
  }

  #bindHeightSync() {
    this.#equalizeHeights();

    if (this.#heightSyncBound) return;
    this.#heightSyncBound = true;

    this.#resizeObserver = new ResizeObserver(() => this.#equalizeHeights());
    this.#resizeObserver.observe(this);
  }

  #handleBreakpoint() {
    if (!this.#mobileMediaQuery.matches) {
      this.#destroyCarousel();
    } else if (this.#hasActivated) {
      this.#initCarousel();
    } else {
      this.#cancelCarouselWait();
    }

    this.#equalizeHeights();
  }

  #initCarousel() {
    if (
      !this.isConnected ||
      !this.#mobileMediaQuery.matches ||
      !this.#hasActivated ||
      this.#carousel ||
      this.#carouselRafId !== null
    ) {
      return;
    }

    const start = () => {
      this.#carouselRafId = null;

      if (
        !this.isConnected ||
        !this.#mobileMediaQuery.matches ||
        !this.#hasActivated ||
        this.#carousel
      ) {
        return;
      }

      if (!window.Carousel) {
        this.#carouselRafId = requestAnimationFrame(start);
        return;
      }

      const carousel = new window.Carousel(this, {
        Dots: false,
        Navigation: false,
        infinite: false,
        center: false,
        fill: false,
        slidesPerPage: 1,
      });

      this.#carousel = carousel;
      this.carousel = carousel;

      carousel.on('ready change settle refresh', this.#syncSlideAccessibility);

      carousel.on('ready', () => {
        if (
          this.#carousel !== carousel ||
          !this.isConnected ||
          !this.#mobileMediaQuery.matches ||
          !this.#hasActivated
        ) {
          return;
        }

        this.classList.add('is-carousel-active');
        this.#equalizeHeights();
      });

      carousel.on('refresh', () => {
        if (this.#carousel === carousel) {
          this.#equalizeHeights();
        }
      });
    };

    start();
  }
}

if (!customElements.get('servico-cliente-slider')) {
  customElements.define('servico-cliente-slider', ServicoClienteSlider);
}
